import { closestCorners, DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Archive, FolderClosed, FolderOpen, Plus } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { QueryErrorDisplay } from "@/lib/components/errors";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useServicesContext } from "../../context/services-context";
import { useDragAndDrop } from "../../hooks/use-drag-and-drop";
import { useDragSensors } from "../../hooks/use-drag-sensors";
import { useSearchExpand } from "../../hooks/use-search-expand";
import { useServicesDialogs } from "../../hooks/use-services-dialogs";
import { useArchiveService, useRestoreService } from "../../hooks/use-services-mutation";
import { useServicesUIStore } from "../../store/use-services-ui-store";
import { ArchiveManagerDialog, CategoryDialog, CreateServiceDialog } from "../dialogs";
import { ServicesEmptyState } from "../shared/services-empty-state";
import { ServicesSearch } from "../shared/services-search";
import { ServicesStats } from "../shared/services-stats";
import { ServicesTable } from "../tables";

export function ServicesPageDesktop() {
  const { t } = useTranslation("services");
  const {
    currentSalon,
    filteredCategories,
    filteredServicesByCategory,
    archivedCategories: archivedCategoriesData,
    archivedServicesByCategory,
    allArchivedServices,
    totalServices,
    totalCategories,
    hasArchivedItems,
    totalArchivedCount,
    loading,
    error,
    retry,
    isUpdatingSortOrder,
    isUpdatingServiceSortOrder,
  } = useServicesContext();
  const {
    expandedCategories,
    searchTerm,
    isDragInProgress,
    serviceDragInProgress,
    expandAllCategories,
    collapseAllCategories,
    setIsDragInProgress,
    setActiveDragId,
    setServiceDragInProgress,
    setActiveServiceDragId,
  } = useServicesUIStore();

  const {
    handleCreateCategory,
    handleEditCategory,
    handleCreateService,
    handleOpenArchiveManager,
    categoryDialogOpen,
    serviceDialogOpen,
    archiveDialogOpen,
    editingCategory,
    selectedCategoryForService,
    setCategoryDialogOpen,
    setServiceDialogOpen,
    setArchiveDialogOpen,
  } = useServicesDialogs();

  // Create trailing elements inline to avoid hook order issues
  const trailingElements = [
    <Button key="service" onClick={() => handleCreateService()}>
      <Plus className="w-4 h-4 mr-2" />
      {t("actions.addService")}
    </Button>,
    <Button key="category" variant="outline" onClick={handleCreateCategory}>
      <FolderOpen className="w-4 h-4 mr-2" />
      {t("actions.addCategory")}
    </Button>,
  ];

  const sensors = useDragSensors({ isMobile: false });

  const { handleDragStart, handleDragEnd, activeDragCategory } = useDragAndDrop();

  // Service archive/restore handlers
  const { execute: archiveService } = useArchiveService();
  const { execute: restoreService } = useRestoreService();

  const handleArchiveService = async (service: any) => {
    if (!currentSalon?.id) return;
    await archiveService(currentSalon.id, service.id);
  };

  const handleRestoreService = async (service: any) => {
    if (!currentSalon?.id) return;
    await restoreService(currentSalon.id, service.id);
  };

  const isAllExpanded =
    filteredCategories.length > 0 && expandedCategories.size === filteredCategories.length;

  // Handle auto-expand logic for search
  useSearchExpand();

  // Cleanup drag state on unmount
  useEffect(() => {
    return () => {
      setIsDragInProgress(false);
      setActiveDragId(null);
      setServiceDragInProgress(false);
      setActiveServiceDragId(null);
    };
  }, [setIsDragInProgress, setActiveDragId, setServiceDragInProgress, setActiveServiceDragId]);

  // Desktop bottom element (search and controls)
  const desktopBottomElement = (
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="flex-1">
        <ServicesSearch variant="desktop" />
      </div>

      <div className="flex items-center gap-2">
        <ServicesStats totalServices={totalServices} totalCategories={totalCategories} />
        <div className="h-4 w-px bg-border" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (isAllExpanded) {
              collapseAllCategories();
            } else {
              expandAllCategories(filteredCategories.map((c) => c.id));
            }
          }}
        >
          {isAllExpanded ? (
            <>
              <FolderClosed className="w-4 h-4" />
              {t("actions.collapseAll")}
            </>
          ) : (
            <>
              <FolderOpen className="w-4 h-4" />
              {t("actions.expandAll")}
            </>
          )}
        </Button>
        {hasArchivedItems && (
          <>
            <div className="h-4 w-px bg-border" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenArchiveManager}
              className="flex items-center gap-2"
            >
              <Archive className="w-4 h-4" />
              {t("actions.archiveWithCount", { count: totalArchivedCount })}
            </Button>
          </>
        )}
      </div>
    </div>
  );

  // Determine content to show
  const isLoading = loading && filteredCategories.length === 0;
  const hasError = error && filteredCategories.length === 0;
  const isEmpty = filteredCategories.length === 0;

  return (
    <>
      <BaseNavigationBar
        title={t("page.title")}
        subtitle={t("page.subtitle")}
        leadElement={undefined}
        trailingElements={trailingElements}
        bottomElement={desktopBottomElement}
      />

      <PageContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-muted-foreground">{t("status.loading")}</span>
          </div>
        ) : hasError ? (
          <div className="flex items-center justify-center py-12">
            <QueryErrorDisplay error={error} onRetry={retry} />
          </div>
        ) : isEmpty ? (
          <ServicesEmptyState totalCategories={totalCategories} />
        ) : (
          <div className="space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredCategories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <ServicesTable
                  categories={filteredCategories}
                  servicesByCategory={filteredServicesByCategory}
                  salonId={currentSalon?.id || ""}
                  expandedCategories={expandedCategories}
                  onToggleCategory={(categoryId) =>
                    useServicesUIStore.getState().toggleCategory(categoryId)
                  }
                  onEditCategory={handleEditCategory}
                  onCreateService={handleCreateService}
                  onDeleteService={() => {}} // Legacy - kept for compatibility
                  onArchiveService={handleArchiveService}
                  onRestoreService={handleRestoreService}
                  isDragInProgress={isDragInProgress}
                  isUpdating={isUpdatingSortOrder}
                  isSearching={!!searchTerm.trim()}
                  isEditMode={false} // Desktop doesn't use edit mode
                  serviceDragInProgress={serviceDragInProgress}
                  isUpdatingServiceSortOrder={isUpdatingServiceSortOrder}
                />
              </SortableContext>

              <DragOverlay dropAnimation={null}>
                {activeDragCategory ? (
                  <div className="bg-background border border-primary/30 rounded-lg shadow-lg p-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: activeDragCategory.color }}
                      />
                      <span className="font-semibold text-lg">{activeDragCategory.name}</span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </PageContent>

      {/* Dialogs */}
      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        category={editingCategory}
        salonId={currentSalon?.id || ""}
        onSuccess={() => {}} // Handled automatically by hooks
      />

      <CreateServiceDialog
        open={serviceDialogOpen}
        onOpenChange={setServiceDialogOpen}
        salonId={currentSalon?.id || ""}
        defaultCategoryId={selectedCategoryForService ?? undefined}
        onSuccess={() => {}} // Handled automatically by hooks
      />

      <ArchiveManagerDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        salonId={currentSalon?.id || ""}
        archivedCategories={archivedCategoriesData}
        archivedServicesByCategory={archivedServicesByCategory}
        allArchivedServices={allArchivedServices}
      />
    </>
  );
}
